import { NavigationGuardWithThis } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useOUserSettingsStore } from '../stores/use-user-settings-store';
import { authenticatedSettingsLoadPromise } from '../composables/use-settings-load-when-authenticated';
import { defaultSettings } from '../composables/use-settings';
import { settingsLoadPromise } from '../composables/use-update-settings';
import { shouldShowMaintenancePage } from '../utils/maintenance-mode';
import {
  clearMaintenanceRedirect,
  getMaintenanceRedirect,
  getQueryRedirect,
  isValidMaintenanceRedirect,
  resolveMaintenanceRedirect,
  saveMaintenanceRedirect,
} from '../utils/maintenance-redirect';

const MAINTENANCE_ROUTE_NAME = 'maintenance';

/**
 * Redirects authenticated users to the maintenance page when maintenance mode is active,
 * unless their role is listed in maintenance_mode_exclude_roles.
 *
 * The original destination is stored in `query.redirect` and sessionStorage so the user
 * is returned to the correct page once maintenance ends, including after reload.
 */
export const maintenanceGuard: NavigationGuardWithThis<undefined> = async (to) => {
  await settingsLoadPromise;
  await authenticatedSettingsLoadPromise;

  const { oUserSettings } = storeToRefs(useOUserSettingsStore());

  const showMaintenance = shouldShowMaintenancePage(oUserSettings.value, {
    maintenance_mode: defaultSettings.maintenance_mode,
    maintenance_mode_exclude_roles: defaultSettings.maintenance_mode_exclude_roles,
  });

  if (to.name === MAINTENANCE_ROUTE_NAME) {
    if (showMaintenance) {
      const fromQuery = getQueryRedirect(to.query.redirect);

      if (fromQuery) {
        // URL already has a redirect — keep it unchanged on reload.
        saveMaintenanceRedirect(fromQuery);
        return;
      }

      const stored = getMaintenanceRedirect();
      if (stored) {
        return { name: MAINTENANCE_ROUTE_NAME, query: { redirect: stored }, replace: true };
      }

      return;
    }

    const redirect = resolveMaintenanceRedirect(to.query.redirect);
    clearMaintenanceRedirect();
    return redirect ? { path: redirect } : { path: '/' };
  }

  if (to.meta.authenticate === false) {
    return;
  }

  if (showMaintenance) {
    if (isValidMaintenanceRedirect(to.fullPath)) {
      saveMaintenanceRedirect(to.fullPath);
      return { name: MAINTENANCE_ROUTE_NAME, query: { redirect: to.fullPath } };
    }

    return { name: MAINTENANCE_ROUTE_NAME };
  }
};
